import { AsyncPipe } from '@angular/common';
import { Component, OnInit, OnChanges, Output, EventEmitter, Input, SimpleChanges, inject, signal, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Validators, ReactiveFormsModule, ValidationErrors, FormBuilder, FormControl, ValidatorFn, AsyncValidatorFn, AbstractControl, FormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import {
  catchError,
  concatMap,
  map,
  switchMap,
  take} from 'rxjs/operators';
import { of, Observable, BehaviorSubject, timer } from 'rxjs';
import { EcaSecurityService, EcaPillarBoxModule, EcaFormColumnModule, EcaDrcInsightLogoModule, DynamicNotification, EcaDynamicNotificationWindowComponent, EcaPassword } from '@drc-eca/eca-components-lib';
import { UserManagementService } from '../../user/services/user-management.service';
import { UserSecurityService } from '../../user/services/user-security.service';

@Component({
    selector: 'app-new-password',
    templateUrl: './new-password.component.html',
    styleUrls: ['./new-password.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [EcaPillarBoxModule, EcaDynamicNotificationWindowComponent, EcaFormColumnModule, EcaDrcInsightLogoModule, ReactiveFormsModule, MatFormField, MatLabel, MatButton, AsyncPipe, EcaPassword]
})
export class NewPasswordComponent implements OnInit, OnChanges {
  private ecaSecurityService = inject(EcaSecurityService);
  private userManagementService = inject(UserManagementService);
  private userSecurityService = inject(UserSecurityService);
  private fb = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  dialog = inject(MatDialog);

  @Input() formTitle!: string;
  @Input() formSubtitle!: string;
  @Input() saveButtonText!: string;
  @Input() userId!: string;
  @Input() token!: string;
  @Input() clientCode?: string;
  @Input() cancelButtonText!: string;
  @Input() formDisabled: boolean = false;
  @Input() notificationSource = signal<DynamicNotification | undefined>(undefined);
  @Output() isLoading: EventEmitter<boolean> = new EventEmitter();
  @Output() cancelEvent: EventEmitter<void> = new EventEmitter();
  // emits the string value of the new password
  @Output() formSubmit: EventEmitter<string> = new EventEmitter();

  profileForm = this.fb.group({
    newPassword: this.fb.control('', { validators: [Validators.required] }),
    newPasswordConfirm: this.fb.control('', { validators: [Validators.required] }),
  }) as FormGroup<{
    newPassword: FormControl<string>;
    newPasswordConfirm: FormControl<string>;
    [key: string]: FormControl<string>;
  }>;
  get newPassword() {
    return this.profileForm.get('newPassword')! as FormControl<string>;
  };
  get newPasswordConfirm() {
    return this.profileForm.get('newPasswordConfirm')! as FormControl<string>;
  };
  minPasswordLength!: number;
  originalValidationRules: any;
  validationRules = new BehaviorSubject({});
  validationRules$ = this.validationRules.asObservable();
  checkStatus = 'INVALID';
  private currentUser: any;

  ngOnInit() {
    this.ecaSecurityService.isUserLoggedIn.pipe(
      concatMap(isLoggedIn => {
        return isLoggedIn ? this.ecaSecurityService.user$ : of(null);
      }),
      switchMap((user) => {
        if (user) {
          this.userId = user.userId;
          this.currentUser = user;
        }

        return (user && !this.token
          ? this.userSecurityService.validatePasswordWithOktaFallback(this.userId, '', undefined, this.clientCode, this.currentUser)
          : this.userManagementService.validatePassword(this.userId, '', this.token, this.clientCode)).pipe(
          catchError(err => {
            console.error(err);
            return of({});
          })
        );
      }),
      take(1)
    ).subscribe((validation) => {
      if (validation.description?.title) {
        this.formTitle = validation.description.title;
      }
      if (validation.description?.subtitle) {
        this.formSubtitle = validation.description.subtitle;
      }
      this.checkStatus = validation.status;

      this.originalValidationRules = validation;
      this.validationRules.next(validation);
      this.isLoading.emit(false);

      this.initializeForm();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    for (const propName in changes) {
      // Programmatically enable/disable the profileForm on formDisabled change
      if (propName === 'formDisabled' && changes[propName].currentValue === false) {
        this.profileForm?.enable();
      }
      if (propName === 'formDisabled' && changes[propName].currentValue === true) {
        this.profileForm?.disable();
      }
    }
  }

  initializeForm() {
    this.profileForm.setValidators([this.pwdMatchValidator]);
    this.newPassword.setAsyncValidators([this.validator]);
    this.newPassword.updateValueAndValidity();
  }

  pwdMatchValidator: ValidatorFn = (): ValidationErrors | null => {
    let response = null;
    let errors: any = {};

    const newPasswordValue = this.newPassword.value;
    const newPasswordConfirmValue = this.newPasswordConfirm.value;

    if (!newPasswordConfirmValue) {
      errors.required = true;
    }

    if (newPasswordValue !== newPasswordConfirmValue) {
      errors.passwordNotEquivalent = true;
      response = { passwordNotEquivalent: true };
    }

    if (!Object.keys(errors).length) {
      errors = null;
    }

    this.newPasswordConfirm.setErrors(errors);

    return response
  }

  validator: AsyncValidatorFn = (control: AbstractControl): Observable<ValidationErrors | null> => {
    const newPasswordValue = control.value;

    return timer(333).pipe(
      switchMap(() => {
        return this.currentUser && !this.token
          ? this.userSecurityService.validatePasswordWithOktaFallback(this.userId, newPasswordValue, undefined, this.clientCode, this.currentUser)
          : this.userManagementService.validatePassword(this.userId, newPasswordValue, this.token, this.clientCode)
      }),
      map((validation) => {
        this.checkStatus = validation.status;
        this.validationRules.next(validation);

        if (validation.status.toLowerCase() === 'invalid') {
          return { checks: true };
        }

        return null;
      }),
      catchError(() => {
        return of(null);
      }),
    )
  }

  onSubmitForm(event: Event) {
    this.isLoading.emit(true);
    this.formSubmit.emit(this.newPasswordConfirm.value);
    // prevent the form submit event from propagating
    event.stopPropagation();
  }

  onCancelClick() {
    this.cancelEvent.emit();
  }
}
